import { useQuery } from "@apollo/client";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ListIcon from "@mui/icons-material/FormatListBulleted";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Link as MuiLink,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { OmnisearchDataDisplay } from "components/OmnisearchDataDisplay";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { getFullName } from "encamp-shared/src/utils/name";
import { TasksQuery, TaskStatus, VirtualEventStatus } from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { OmnisearchProvider } from "providers/OmnisearchContext";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { prettyPrintDateMed } from "util/dates";

import { TASKS_COUNT_QUERY, TASKS_QUERY } from "../../Tasks/schema";
import { taskStatusToChipSx } from "../../Tasks/statusStyles";
import { TasksCalendar } from "../../Tasks/TasksCalendar";
import { buildBeforeAfterSearchString } from "../../Tasks/util.buildSearchString";
import { taskStatusToLabel } from "../../Tasks/util.labels";
import { TasksWeekCalendar } from "./TasksWeekCalendar";

type Row = TasksQuery["virtualEvents"]["items"][0];

type ViewMode = "list" | "week" | "calendar";

function TaskStatCard({
  icon,
  label,
  count,
  loading,
}: {
  icon: string;
  label: string;
  count: number;
  loading: boolean;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        width: 200,
        height: 52,
        display: "flex",
        alignItems: "center",
        backgroundColor: grey[50],
        borderRadius: 0,
      }}
    >
      <CardContent sx={{ pt: "4px", pb: "4px", px: "16px", "&:last-child": { pb: "4px" } }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1, display: "block", mb: "2px" }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={30} />
        ) : (
          <Typography variant="body2" fontWeight="bold">
            {icon} {count}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export const TasksSection: React.FC = () => {
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const baseSearch = `tenantId:${tenantId} facilityId:${facilityId}`;
  const fourteenDaysFromNow = DateTime.local().plus({ days: 14 }).toISODate();

  const { data: overdueData, loading: overdueLoading } = useQuery(TASKS_COUNT_QUERY, {
    variables: { search: `${baseSearch} status:OVERDUE` },
  });

  const { data: dueSoonData, loading: dueSoonLoading } = useQuery(TASKS_COUNT_QUERY, {
    variables: { search: `${baseSearch} status:ACTIVE before:${fourteenDaysFromNow}` },
  });

  const [viewMode, setViewMode] = useState<ViewMode>("list");

  const [calendarRange, setCalendarRange] = useState<string>(() =>
    buildBeforeAfterSearchString(DateTime.local())
  );

  const [weekRange, setWeekRange] = useState<string>(() => {
    const start = DateTime.local().startOf("week").minus({ days: 1 });
    return `after:${start.toISODate()} before:${start.plus({ days: 6 }).toISODate()}`;
  });

  useEffect(() => {
    setCalendarRange(buildBeforeAfterSearchString(DateTime.local()));
  }, []);

  const onChangeViewMode = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newViewMode: ViewMode | null) => {
      if (newViewMode !== null) {
        setViewMode(newViewMode);
      }
    },
    []
  );

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "deadline",
      headerName: "Deadline",
      sortable: true,
      flex: 0.7,
      renderCell: ({ value }) => <CellText variant="body2">{prettyPrintDateMed(value)}</CellText>,
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      sortable: true,
      renderCell: ({ row: { title } }) => <CellText variant="body2">{title}</CellText>,
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: 1.5,
      sortable: true,
      renderCell({ row }) {
        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {(row.facilities ?? []).map((facility) => (
              <Typography
                key={facility.id}
                variant="body2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={facility.name}>
                  <span>{facility.name}</span>
                </Tooltip>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "taskStatus",
      headerName: "Status",
      flex: 0.7,
      sortable: true,
      renderCell({ row: { taskStatus, status } }) {
        if (status === VirtualEventStatus.Overdue) {
          return (
            <Chip
              label="Overdue"
              sx={{
                fontSize: 12,
                backgroundColor: `${theme.palette.error.light}20`,
                color: theme.palette.error.main,
              }}
              size="small"
            />
          );
        }
        const stylingBlock = taskStatusToChipSx(taskStatus, theme);
        return <Chip label={taskStatusToLabel(taskStatus)} sx={stylingBlock} size="small" />;
      },
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(TaskStatus),
      enumPresentationFunction: (enumValue) => taskStatusToLabel(enumValue),
    },
    {
      field: "assignee",
      headerName: "Assignee",
      sortable: true,
      flex: 1,
      valueGetter(_, row) {
        return getFullName(row.assignee?.person) || row.assignee?.email;
      },
      renderCell: ({ value }) => <CellText variant="body2">{value}</CellText>,
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: 0.7,
      filterKeyType: "taskTags",
      renderCell: ({ row }) => (
        <TagsDisplay tags={row.tags?.map((tag) => ({ id: tag.id, name: tag.name })) ?? []} />
      ),
    },
  ];

  const additionalFilterColumns: FilterKey[] = [
    { key: "after", header: "Due After", filterKeyType: "date" },
    { key: "before", header: "Due Before", filterKeyType: "date" },
  ];

  const viewModeToggle = [
    <ToggleButtonGroup
      key="view-mode-toggle-button-group"
      value={viewMode}
      exclusive
      color="primary"
      size="large"
      onChange={onChangeViewMode}
    >
      <ToggleButton value="list">
        <ListIcon />
      </ToggleButton>
      <ToggleButton value="calendar">
        <CalendarIcon />
      </ToggleButton>
      <ToggleButton value="week">
        <CalendarViewWeekIcon />
      </ToggleButton>
    </ToggleButtonGroup>,
  ];

  const commandButtons = () => [] as React.ReactNode[];

  const defaultSearch = `tenantId:${tenantId} facilityId:${facilityId}`;

  return (
    <>
      <Typography variant="h5" mb={1}>
        Facility Tasks
      </Typography>
      <Typography variant="body2" color="text.primary" mb={3}>
        Showing all current facility tasks. To see all tasks or add a new one, open{" "}
        <MuiLink component={Link} to={`/o/${tenantId}/tasks`} underline="none">
          Tasks
        </MuiLink>
        .
      </Typography>
      <Stack direction="row" spacing="16px" sx={{ mb: "24px" }}>
        <TaskStatCard
          icon="🖍️"
          label="Overdue"
          count={overdueData?.virtualEvents.count ?? 0}
          loading={overdueLoading}
        />
        <TaskStatCard
          icon="💥"
          label="Due within 14 days"
          count={dueSoonData?.virtualEvents.count ?? 0}
          loading={dueSoonLoading}
        />
      </Stack>
      <Box sx={{ mx: -3 }}>
        {viewMode === "list" && (
          <OmnisearchDataGrid
            persistenceKey={`facility-tasks-v2-${facilityId}`}
            noDataMessage="No tasks have been added for this facility yet."
            isURLDriven
            columns={columns}
            initialSortModel={[{ field: "deadline", sort: "asc" }]}
            dataQuery={TASKS_QUERY}
            defaultSearch={defaultSearch}
            getItems={(data) => data.virtualEvents.items}
            getCount={(data) => data.virtualEvents.count ?? 0}
            excludeFilterColumns={["deadline", "facilities"]}
            additionalFilterColumns={additionalFilterColumns}
            isRowSelectable={() => false}
            startCommandButtons={viewModeToggle}
            commandButtons={commandButtons}
            showManageColumns={false}
            initialPageSize={10}
            rowHeight="auto"
            columnVisibilityModel={{
              ...(isMobile && {
                facilities: false,
                tags: false,
              }),
            }}
            gridSx={{
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              "& .MuiDataGrid-row:hover": { cursor: "default" },
            }}
          />
        )}
        {viewMode === "week" && (
          <OmnisearchProvider>
            <OmnisearchDataDisplay
              persistenceKey={`facility-tasks-week-v2-${facilityId}`}
              isURLDriven
              columns={columns}
              dataQuery={TASKS_QUERY}
              dataQueryVariables={{ pageSize: undefined, page: undefined }}
              defaultSearch={`${defaultSearch} ${weekRange}`}
              getItems={(data) => data.virtualEvents.items}
              getCount={(data) => data.virtualEvents.count ?? 0}
              excludeFilterColumns={["deadline", "facilities"]}
              additionalFilterColumns={additionalFilterColumns}
              startCommandButtons={viewModeToggle}
              commandButtons={commandButtons}
              dataDisplay={({ items, loading }) => (
                <TasksWeekCalendar
                  loading={loading}
                  tasks={items}
                  onWeekChange={(start, end) =>
                    setWeekRange(`after:${start.toISODate()} before:${end.toISODate()}`)
                  }
                />
              )}
            />
          </OmnisearchProvider>
        )}
        {viewMode === "calendar" && (
          <OmnisearchProvider>
            <OmnisearchDataDisplay
              persistenceKey={`facility-tasks-calendar-v2-${facilityId}`}
              isURLDriven
              columns={columns}
              dataQuery={TASKS_QUERY}
              dataQueryVariables={{ pageSize: undefined, page: undefined }}
              defaultSearch={`${defaultSearch} ${calendarRange}`}
              getItems={(data) => data.virtualEvents.items}
              getCount={(data) => data.virtualEvents.count ?? 0}
              excludeFilterColumns={["deadline", "facilities"]}
              additionalFilterColumns={additionalFilterColumns}
              startCommandButtons={viewModeToggle}
              commandButtons={commandButtons}
              dataDisplay={({ items, loading }) => (
                <TasksCalendar
                  loading={loading}
                  tasks={items}
                  onTaskClick={() => {}}
                  onRangeChange={setCalendarRange}
                />
              )}
            />
          </OmnisearchProvider>
        )}
      </Box>
    </>
  );
};
